/**
 * Proship TAT Integration
 * Handles: pincode input, API call, TAT display
 * Works on: Product Page + Cart Page
 */

(function () {
  'use strict';

  // ─── CONFIG ────────────────────────────────────────────────────────────────
  const PROSHIP_CONFIG = {
    merchantId: '6a33fb1714f80a6195d5bcdc',
    pickupPincode: 122506,
    tatApiUrl: 'https://rqkp8eydpl.execute-api.ap-south-1.amazonaws.com/prod/proship-tat',
    storageKey: 'proship_customer_pincode',
    cacheTTL: 30 * 60 * 1000, // 30 minutes cache per pincode
  };
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Validate pincode — must be exactly 6 digits
   */
  function isValidPincode(pin) {
    return /^\d{6}$/.test(String(pin).trim());
  }

  /**
   * Get cached TAT for a pincode (avoids redundant API calls)
   */
  function getCachedTAT(pincode) {
    try {
      const raw = sessionStorage.getItem(PROSHIP_CONFIG.storageKey + '_' + pincode);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts > PROSHIP_CONFIG.cacheTTL) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  /**
   * Cache TAT response for a pincode
   */
  function cacheTAT(pincode, data) {
    try {
      sessionStorage.setItem(
        PROSHIP_CONFIG.storageKey + '_' + pincode,
        JSON.stringify({ ts: Date.now(), data })
      );
    } catch (e) {}
  }

  /**
   * Save last used pincode so it persists across product/cart pages
   */
  function saveLastPincode(pincode) {
    try {
      sessionStorage.setItem(PROSHIP_CONFIG.storageKey + '_last', pincode);
    } catch (e) {}
  }

  function getLastPincode() {
    try {
      return sessionStorage.getItem(PROSHIP_CONFIG.storageKey + '_last') || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Call Proship TAT API
   */
  async function fetchTAT(dropPincode) {
    const cached = getCachedTAT(dropPincode);
    if (cached) return cached;

    const payload = {
      queryStringParam: {
        pickup_pincode: PROSHIP_CONFIG.pickupPincode,
        drop_pincode: parseInt(dropPincode, 10),
        merchantid: PROSHIP_CONFIG.merchantId,
      },
    };

    const response = await fetch(PROSHIP_CONFIG.tatApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('API error: ' + response.status);
    }

    const data = await response.json();
    cacheTAT(dropPincode, data);
    return data;
  }

  /**
   * Format TAT response into human readable string
   * e.g. "Delivery in 3–5 business days"
   */
  function formatTATMessage(data) {
    const min = data.overallMinTAT;
    const max = data.overallMaxTAT;

    if (!min && !max) return null;

    if (min === max) {
      return `Delivery in <span class="tat-days">${min} business day${min > 1 ? 's' : ''}</span> 🚚`;
    }
    return `Delivery in <span class="tat-days">${min}–${max} business days</span> 🚚`;
  }

  /**
   * Show result in UI
   */
  function showResult(context, html) {
    const resultEl = document.getElementById('proship-tat-result-' + context);
    const textEl = document.getElementById('proship-tat-result-text-' + context);
    const errorEl = document.getElementById('proship-tat-error-' + context);

    if (errorEl) errorEl.style.display = 'none';
    if (textEl) textEl.innerHTML = html;
    if (resultEl) resultEl.style.display = 'block';
  }

  /**
   * Show error in UI
   */
  function showError(context, message) {
    const resultEl = document.getElementById('proship-tat-result-' + context);
    const errorEl = document.getElementById('proship-tat-error-' + context);
    const errorTextEl = document.getElementById('proship-tat-error-text-' + context);

    if (resultEl) resultEl.style.display = 'none';
    if (errorTextEl) errorTextEl.textContent = message;
    if (errorEl) errorEl.style.display = 'block';
  }

  /**
   * Show loading state
   */
  function setLoading(context, loading) {
    const btn = document.getElementById('proship-check-btn-' + context);
    if (!btn) return;
    btn.disabled = loading;
    btn.textContent = loading ? 'Checking...' : 'Check';
  }

  /**
   * Main handler — triggered on Check button click or Enter key
   */
  async function handleCheck(context) {
    const input = document.getElementById('proship-pincode-input-' + context);
    if (!input) return;

    const pincode = input.value.trim();

    if (!isValidPincode(pincode)) {
      showError(context, 'Please enter a valid 6-digit pincode.');
      return;
    }

    setLoading(context, true);

    try {
      const data = await fetchTAT(pincode);
      const message = formatTATMessage(data);

      if (message) {
        saveLastPincode(pincode);
        showResult(context, message);
        // Sync pincode across all instances on the page
        syncAllInstances(pincode, message);
      } else {
        showError(context, 'Delivery not available for this pincode.');
      }
    } catch (err) {
      console.error('[Proship TAT]', err);
      showError(context, 'Unable to fetch delivery info. Please try again.');
    } finally {
      setLoading(context, false);
    }
  }

  /**
   * Sync pincode & result across multiple TAT instances on same page
   * (e.g. if both product and cart widgets are present)
   */
  function syncAllInstances(pincode, message) {
    const contexts = ['product', 'cart'];
    contexts.forEach(function (ctx) {
      const input = document.getElementById('proship-pincode-input-' + ctx);
      if (input && input.value !== pincode) {
        input.value = pincode;
        showResult(ctx, message);
      }
    });
  }

  /**
   * Initialize a TAT widget for a given context
   */
  function initWidget(context) {
    const wrapper = document.getElementById('proship-tat-' + context);
    if (!wrapper) return;

    const input = document.getElementById('proship-pincode-input-' + context);
    const btn = document.getElementById('proship-check-btn-' + context);

    if (!input || !btn) return;

    // Pre-fill from last used pincode
    const lastPin = getLastPincode();
    if (lastPin && isValidPincode(lastPin)) {
      input.value = lastPin;
      // Auto-check silently if already cached
      const cached = getCachedTAT(lastPin);
      if (cached) {
        const message = formatTATMessage(cached);
        if (message) showResult(context, message);
      }
    }

    // Only allow numeric input
    input.addEventListener('input', function () {
      this.value = this.value.replace(/[^0-9]/g, '');
    });

    // Check on Enter key
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCheck(context);
      }
    });

    // Check on button click
    btn.addEventListener('click', function () {
      handleCheck(context);
    });
  }

  /**
   * Boot — initialize all widgets when DOM is ready
   */
  function boot() {
    initWidget('product');
    initWidget('cart');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();